import {useEffect, useState} from 'react';
import type {CSSProperties} from 'react';
import {ApiError, api, authMode, loginUrl} from './api';
import type {
  Block,
  BlockDefinition,
  ContentEntry,
  MediaAsset,
  Page,
  PageRevision,
  PluginDefinition,
  PropertySchema,
  Site,
  SiteDomain,
  SitePlugin,
  Theme,
} from './types';

const defaultColors: Record<string, string> = {
  primary: '#4F46E5',
  secondary: '#111827',
  accent: '#14B8A6',
  background: '#FFFFFF',
  surface: '#F3F4F6',
  text: '#111827',
  muted: '#6B7280',
  border: '#E5E7EB',
};

export function App() {
  const [connected, setConnected] = useState(
    authMode === 'session' || Boolean(localStorage.getItem('impakt_access_token')),
  );
  const [sites, setSites] = useState<Site[]>([]);
  const [blocks, setBlocks] = useState<BlockDefinition[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedSite = sites.find((site) => site.id === selectedId) ?? null;

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const [siteList, blockList] = await Promise.all([api.sites(), api.blocks()]);
      setSites(siteList);
      setBlocks(blockList);
      setSelectedId((current) => current ?? siteList[0]?.id ?? null);
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) {
        setConnected(false);
      }
      setError(reason instanceof Error ? reason.message : 'Nu am putut încărca Website Studio.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (connected) void load();
  }, [connected]);

  useEffect(() => {
    if (!selectedId) {
      setPages([]);
      return;
    }
    void api.pages(selectedId).then(setPages).catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : 'Nu am putut încărca paginile.');
    });
  }, [selectedId]);

  const refreshSite = async (siteId: string) => {
    const [site, sitePages] = await Promise.all([api.site(siteId), api.pages(siteId)]);
    setSites((current) => current.map((item) => item.id === site.id ? site : item));
    setPages(sitePages);
  };

  if (!connected) {
    return <ConnectionScreen onConnect={() => setConnected(true)} />;
  }

  return (
    <div className="studio-shell">
      <aside className="main-nav">
        <div className="brand-mark">I</div>
        {['▦', '▤', '◈', '◫', '⌁'].map((icon, index) => (
          <button className={index === 0 ? 'nav-icon active' : 'nav-icon'} key={icon}>{icon}</button>
        ))}
        <button
          className="nav-icon account"
          onClick={() => {
            if (authMode === 'token') {
              localStorage.removeItem('impakt_access_token');
              setConnected(false);
              return;
            }
            void api.logout().finally(() => { window.location.href = loginUrl; });
          }}
          title="Deconectare"
        >RV</button>
      </aside>

      <section className="workspace">
        <header className="workspace-header">
          <div>
            <span className="product-label">Impakt Platform</span>
            <h1>Website Studio</h1>
          </div>
          {selectedSite && <StatusPill site={selectedSite} />}
        </header>

        {error && <div className="error-banner"><span>{error}</span><button onClick={() => setError(null)}>×</button></div>}
        {busy && sites.length === 0 ? <Loading /> : null}

        {!busy && sites.length === 0 ? (
          <CreateSite onCreated={(site) => { setSites([site]); setSelectedId(site.id); }} />
        ) : null}

        {selectedSite ? (
          <SiteEditor
            site={selectedSite}
            pages={pages}
            definitions={blocks}
            onSelectSite={setSelectedId}
            sites={sites}
            onRefresh={() => refreshSite(selectedSite.id)}
            onSiteChanged={(site) => setSites((current) => current.map((item) => item.id === site.id ? site : item))}
            onError={setError}
          />
        ) : null}
      </section>
    </div>
  );
}

function ConnectionScreen({onConnect}: {onConnect: () => void}) {
  const [token, setToken] = useState('');
  if (authMode === 'session') {
    return (
      <main className="connection-screen">
        <div className="connection-card">
          <div className="brand-mark large">I</div>
          <span className="product-label">Impakt Platform</span>
          <h1>Sesiune necesară</h1>
          <p>Website Studio folosește autentificarea și tenantul selectat în Impakt Admin.</p>
          <a className="primary-button login-link" href={loginUrl}>Intră în Impakt Admin</a>
        </div>
      </main>
    );
  }
  return (
    <main className="connection-screen">
      <div className="connection-card">
        <div className="brand-mark large">I</div>
        <span className="product-label">Impakt Platform</span>
        <h1>Website Studio</h1>
        <p>Conectează sesiunea tenantului pentru a administra site-urile, temele și publicațiile.</p>
        <label>Access token<input type="password" value={token} onChange={(event) => setToken(event.target.value)} /></label>
        <button className="primary-button" disabled={!token.trim()} onClick={() => {
          localStorage.setItem('impakt_access_token', token.trim());
          onConnect();
        }}>Deschide studioul</button>
      </div>
    </main>
  );
}

function CreateSite({onCreated}: {onCreated: (site: Site) => void}) {
  const [name, setName] = useState('Site nou');
  const [key, setKey] = useState('site-nou');
  const [saving, setSaving] = useState(false);
  return (
    <div className="empty-state">
      <div className="empty-illustration">✦</div>
      <h2>Construiește primul site</h2>
      <p>Începe cu identitatea site-ului. Paginile, tema și blocurile se configurează apoi vizual.</p>
      <div className="inline-form">
        <label>Nume<input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Identificator<input value={key} onChange={(event) => setKey(event.target.value.toLowerCase())} /></label>
      </div>
      <button className="primary-button" disabled={saving} onClick={async () => {
        setSaving(true);
        try { onCreated(await api.createSite({name, key, default_locale: 'ro'})); } finally { setSaving(false); }
      }}>{saving ? 'Se creează…' : 'Creează site-ul'}</button>
    </div>
  );
}

function SiteEditor(props: {
  site: Site;
  sites: Site[];
  pages: Page[];
  definitions: BlockDefinition[];
  onSelectSite: (id: string) => void;
  onRefresh: () => Promise<void>;
  onSiteChanged: (site: Site) => void;
  onError: (error: string) => void;
}) {
  const {site, sites, pages, definitions, onSelectSite, onRefresh, onSiteChanged, onError} = props;
  const [pageId, setPageId] = useState<string | null>(pages[0]?.id ?? null);
  const sourcePage = pages.find((page) => page.id === pageId) ?? pages[0] ?? null;
  const [page, setPage] = useState<Page | null>(sourcePage);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [panel, setPanel] = useState<'properties' | 'theme' | 'media' | 'history' | 'plugins' | 'domains'>('properties');
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [creatingPage, setCreatingPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [workspaceMode, setWorkspaceMode] = useState<'pages' | 'articles'>('pages');

  useEffect(() => {
    const next = pages.find((item) => item.id === pageId) ?? pages[0] ?? null;
    setPage(next);
    setPageId(next?.id ?? null);
    setSelectedBlockId(null);
  }, [pages, pageId]);

  const selectedBlock = page?.layout.find((block) => block.id === selectedBlockId) ?? null;
  const selectedDefinition = definitions.find((definition) => definition.type === selectedBlock?.type) ?? null;

  const mutateBlock = (id: string, mutation: (block: Block) => Block) => {
    setPage((current) => current ? {...current, layout: current.layout.map((block) => block.id === id ? mutation(block) : block)} : current);
  };

  const addBlock = (definition: BlockDefinition) => {
    if (!page) return;
    const props: Record<string, unknown> = {};
    for (const name of definition.property_schema.required ?? []) {
      const schema = definition.property_schema.properties?.[name];
      props[name] = defaultPropertyValue(schema);
    }
    const block: Block = {
      id: `block_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`,
      type: definition.type,
      props,
      styles: {},
    };
    setPage({...page, layout: [...page.layout, block]});
    setSelectedBlockId(block.id);
  };

  const savePage = async () => {
    if (!page) return;
    setSaving(true);
    try {
      await api.updatePage(site, page);
      await onRefresh();
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Pagina nu a putut fi salvată.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editor-frame">
      <div className="editor-toolbar">
        <select value={site.id} onChange={(event) => onSelectSite(event.target.value)}>
          {sites.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
        </select>
        <div className="breadcrumbs"><span>{workspaceMode === 'pages' ? 'Site' : 'Editorial'}</span><b>/</b><strong>{workspaceMode === 'pages' ? page?.title ?? 'Fără pagini' : 'Articole'}</strong></div>
        <div className="workspace-switcher">
          <button className={workspaceMode === 'pages' ? 'active' : ''} onClick={() => setWorkspaceMode('pages')}>Pagini</button>
          <button className={workspaceMode === 'articles' ? 'active' : ''} onClick={() => setWorkspaceMode('articles')}>Articole</button>
        </div>
        {workspaceMode === 'pages' ? <>
        <div className="device-switcher">
          {(['desktop', 'tablet', 'mobile'] as const).map((value) => (
            <button className={device === value ? 'active' : ''} onClick={() => setDevice(value)} key={value}>
              {value === 'desktop' ? '▱' : value === 'tablet' ? '▯' : '▯'}
            </button>
          ))}
        </div>
        <button className="secondary-button" disabled={!page || saving} onClick={savePage}>{saving ? 'Se salvează…' : 'Salvează'}</button>
        {page ? <a className="secondary-button preview-link" href={`/api/v1/site-builder/sites/${site.id}/preview?slug=${encodeURIComponent(page.slug)}&locale=${encodeURIComponent(page.locale)}`} target="_blank" rel="noreferrer">Preview draft</a> : null}
        {site.published_at ? <a className="secondary-button preview-link" href={`/published-sites/${site.key}`} target="_blank" rel="noreferrer">Site public</a> : null}
        <button className="publish-button" disabled={!page || saving} onClick={async () => {
          try {
            if (page) await api.updatePage(site, page);
            const freshSite = await api.site(site.id);
            onSiteChanged(await api.publish(freshSite));
            await onRefresh();
          } catch (reason) {
            onError(reason instanceof Error ? reason.message : 'Site-ul nu a putut fi publicat.');
          }
        }}>Publică</button></> : null}
      </div>

      {workspaceMode === 'articles' ? <ArticleWorkspace onError={onError} /> : <div className="editor-body">
        <aside className="resource-panel">
          <div className="panel-heading"><h3>Pagini</h3><button onClick={() => setCreatingPage((value) => !value)}>＋</button></div>
          {creatingPage ? (
            <div className="new-page-form">
              <input placeholder="Titlul paginii" value={newPageTitle} onChange={(event) => {
                setNewPageTitle(event.target.value);
                setNewPageSlug(slugify(event.target.value));
              }} />
              <input placeholder="slug-pagina" value={newPageSlug} onChange={(event) => setNewPageSlug(slugify(event.target.value))} />
              <button disabled={!newPageTitle.trim() || !newPageSlug.trim()} onClick={async () => {
                try {
                  const created = await api.createPage(site, {
                    title: newPageTitle.trim(), slug: newPageSlug, locale: site.default_locale,
                  });
                  setCreatingPage(false);
                  setNewPageTitle('');
                  setNewPageSlug('');
                  await onRefresh();
                  setPageId(created.id);
                } catch (reason) {
                  onError(reason instanceof Error ? reason.message : 'Pagina nu a putut fi creată.');
                }
              }}>Creează</button>
            </div>
          ) : null}
          {pages.map((item) => (
            <button className={item.id === page?.id ? 'page-row active' : 'page-row'} key={item.id} onClick={() => setPageId(item.id)}>
              <span>▤</span><div><strong>{item.title}</strong><small>/{item.slug === 'home' ? '' : item.slug}</small></div>
            </button>
          ))}
          {pages.length === 0 ? (
            <button className="create-page" onClick={async () => {
              try { await api.createHome(site); await onRefresh(); } catch (reason) {
                onError(reason instanceof Error ? reason.message : 'Pagina nu a putut fi creată.');
              }
            }}>＋ Creează pagina Acasă</button>
          ) : null}
          <div className="panel-heading blocks-title"><h3>Blocuri</h3></div>
          <div className="block-library">
            {definitions.map((definition) => (
              <button key={definition.type} onClick={() => addBlock(definition)}>
                <span>{blockIcon(definition.type)}</span><b>{definition.label}</b><small>{definition.category}</small>
              </button>
            ))}
          </div>
        </aside>

        <main className="canvas-area">
          <div className={`site-canvas ${device}`} style={themeVariables(site.theme)}>
            <div className="preview-nav"><strong>{site.name}</strong><span>Acasă&nbsp;&nbsp; Despre&nbsp;&nbsp; Contact</span></div>
            {page?.layout.length ? page.layout.map((block, index) => (
              <div
                className={selectedBlockId === block.id ? 'canvas-block selected' : 'canvas-block'}
                draggable
                key={block.id}
                onDragStart={() => setDraggedIndex(index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  if (!page || draggedIndex === null || draggedIndex === index) return;
                  const layout = [...page.layout];
                  const [moved] = layout.splice(draggedIndex, 1);
                  layout.splice(index, 0, moved);
                  setPage({...page, layout});
                  setDraggedIndex(null);
                }}
                onClick={() => { setSelectedBlockId(block.id); setPanel('properties'); }}
              >
                <span className="drag-handle">⠿</span>
                <PreviewBlock block={block} />
              </div>
            )) : <div className="canvas-empty"><span>＋</span><h3>Adaugă primul bloc</h3><p>Alege un bloc din panoul din stânga.</p></div>}
          </div>
        </main>

        <aside className="inspector-panel">
          <div className="inspector-tabs">
            <button className={panel === 'properties' ? 'active' : ''} onClick={() => setPanel('properties')}>Conținut</button>
            <button className={panel === 'theme' ? 'active' : ''} onClick={() => setPanel('theme')}>Temă</button>
            <button className={panel === 'media' ? 'active' : ''} onClick={() => setPanel('media')}>Media</button>
            <button className={panel === 'history' ? 'active' : ''} onClick={() => setPanel('history')}>Istoric</button>
            <button className={panel === 'plugins' ? 'active' : ''} onClick={() => setPanel('plugins')}>Pluginuri</button>
            <button className={panel === 'domains' ? 'active' : ''} onClick={() => setPanel('domains')}>Domenii</button>
          </div>
          {panel === 'history' && page ? (
            <RevisionPanel site={site} page={page} onRefresh={onRefresh} onError={onError} />
          ) : panel === 'media' ? (
            <MediaPanel site={site} onError={onError} onPick={(asset) => {
              if (!page) return;
              if (selectedBlock?.type === 'core.image') {
                mutateBlock(selectedBlock.id, (block) => ({
                  ...block,
                  props: {...block.props, source: asset.url, alt: asset.alt_text},
                }));
                setPanel('properties');
                return;
              }
              const block: Block = {
                id: `block_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`,
                type: 'core.image',
                props: {source: asset.url, alt: asset.alt_text, caption: ''},
                styles: {},
              };
              setPage({...page, layout: [...page.layout, block]});
              setSelectedBlockId(block.id);
              setPanel('properties');
            }} />
          ) : panel === 'plugins' ? (
            <PluginsPanel site={site} onRefresh={onRefresh} onError={onError} />
          ) : panel === 'domains' ? (
            <DomainsPanel site={site} onRefresh={onRefresh} onError={onError} />
          ) : panel === 'theme' ? (
            <ThemePanel site={site} onSaved={async (theme) => {
              try { onSiteChanged(await api.updateSite(site, theme)); } catch (reason) {
                onError(reason instanceof Error ? reason.message : 'Tema nu a putut fi salvată.');
              }
            }} />
          ) : selectedBlock && selectedDefinition ? (
            <BlockInspector
              block={selectedBlock}
              definition={selectedDefinition}
              onChange={(next) => mutateBlock(selectedBlock.id, () => next)}
              onDelete={() => {
                if (!page) return;
                setPage({...page, layout: page.layout.filter((item) => item.id !== selectedBlock.id)});
                setSelectedBlockId(null);
              }}
            />
          ) : <div className="inspector-empty"><span>◇</span><p>Selectează un bloc pentru a-i edita conținutul și stilul.</p></div>}
        </aside>
      </div>}
    </div>
  );
}

const emptyArticle = (): ContentEntry => ({
  id: '', type: 'article', slug: '', locale: 'ro', title: '', status: 'draft', version: 1,
  created_at: '', updated_at: '', published_at: null,
  content: {category: 'Noutăți', excerpt: '', body: '', author: 'Redacția Benchmark', reading_time: '5 min', image_url: ''},
});

function ArticleWorkspace({onError}: {onError: (message: string) => void}) {
  const [entries, setEntries] = useState<ContentEntry[]>([]);
  const [selected, setSelected] = useState<ContentEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async (selectedId?: string) => {
    try {
      const items = await api.content('article');
      setEntries(items);
      setSelected((current) => items.find((item) => item.id === (selectedId ?? current?.id)) ?? items[0] ?? null);
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Articolele nu au putut fi încărcate.');
    }
  };
  useEffect(() => { void load(); }, []);

  const setField = (name: string, value: string) => setSelected((current) => current ? {
    ...current, content: {...current.content, [name]: value},
  } : current);
  const mutate = async (operation: () => Promise<ContentEntry>) => {
    setBusy(true);
    try {
      const result = await operation();
      await load(result.id);
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Operația editorială nu a reușit.');
    } finally { setBusy(false); }
  };

  return <div className="article-workspace">
    <aside className="article-list">
      <div className="panel-heading"><h3>Articole</h3><button onClick={() => setSelected(emptyArticle())}>＋</button></div>
      <div className="article-filters"><span>{entries.length} articole</span><a href="/articole" target="_blank" rel="noreferrer">Vezi site-ul ↗</a></div>
      {entries.map((entry) => <button className={selected?.id === entry.id ? 'article-row active' : 'article-row'} key={entry.id} onClick={() => setSelected(entry)}>
        <span className={`content-state ${entry.status}`} />
        <div><strong>{entry.title}</strong><small>{String(entry.content.category ?? 'Articol')} · {entry.status === 'published' ? 'Publicat' : entry.status === 'archived' ? 'Arhivat' : 'Draft'}</small></div>
      </button>)}
    </aside>
    <main className="article-editor">
      {!selected ? <div className="canvas-empty"><span>✎</span><h3>Primul articol</h3><p>Apasă + pentru a începe.</p></div> : <>
        <header className="article-editor-header">
          <div><span className="section-kicker">{selected.id ? `Versiunea ${selected.version}` : 'Articol nou'}</span><h2>{selected.id ? selected.title : 'Creează articol'}</h2></div>
          <div className="article-actions">
            {selected.id && selected.status !== 'archived' ? <button className="secondary-button" disabled={busy} onClick={() => void mutate(() => api.archiveContent(selected))}>Arhivează</button> : null}
            {selected.id && selected.status === 'published' ? <button className="secondary-button" disabled={busy} onClick={() => void mutate(() => api.unpublishContent(selected))}>Retrage</button> : null}
            <button className="secondary-button" disabled={busy || !selected.title.trim()} onClick={() => void mutate(async () => {
              if (selected.id) return api.updateContent(selected);
              return api.createContent({type: 'article', slug: selected.slug || slugify(selected.title), locale: 'ro', title: selected.title, content: selected.content});
            })}>{busy ? 'Se salvează…' : 'Salvează draft'}</button>
            {selected.id && selected.status !== 'archived' ? <button className="publish-button" disabled={busy} onClick={() => void mutate(async () => {
              const saved = await api.updateContent(selected);
              return api.publishContent(saved);
            })}>Publică</button> : null}
          </div>
        </header>
        <div className="article-form">
          <label className="wide">Titlu<input value={selected.title} disabled={selected.status === 'archived'} onChange={(event) => setSelected({...selected, title: event.target.value, slug: selected.id ? selected.slug : slugify(event.target.value)})} /></label>
          <label>Slug<input value={selected.slug} disabled={Boolean(selected.id)} onChange={(event) => setSelected({...selected, slug: slugify(event.target.value)})} /></label>
          <label>Categorie<input value={String(selected.content.category ?? '')} onChange={(event) => setField('category', event.target.value)} /></label>
          <label>Autor<input value={String(selected.content.author ?? '')} onChange={(event) => setField('author', event.target.value)} /></label>
          <label>Timp de citire<input value={String(selected.content.reading_time ?? '')} onChange={(event) => setField('reading_time', event.target.value)} /></label>
          <label className="wide">Imagine reprezentativă (URL)<input value={String(selected.content.image_url ?? '')} onChange={(event) => setField('image_url', event.target.value)} placeholder="https://…" /></label>
          <label className="wide">Rezumat<textarea value={String(selected.content.excerpt ?? '')} onChange={(event) => setField('excerpt', event.target.value)} /></label>
          <label className="wide">Conținut<textarea className="article-body-input" value={String(selected.content.body ?? '')} onChange={(event) => setField('body', event.target.value)} placeholder="Scrie articolul aici. Paragrafele se separă cu o linie liberă." /></label>
          {selected.id ? <small className="article-meta">Actualizat {formatDate(selected.updated_at)} · /articole/{selected.slug}</small> : null}
        </div>
      </>}
    </main>
  </div>;
}

function MediaPanel({site, onPick, onError}: {
  site: Site; onPick: (asset: MediaAsset) => void; onError: (message: string) => void;
}) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadMedia = async () => {
    try { setAssets(await api.media(site.id)); } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Biblioteca media nu a putut fi încărcată.');
    }
  };
  useEffect(() => { void loadMedia(); }, [site.id]);

  return (
    <div className="inspector-content settings-panel">
      <span className="section-kicker">Bibliotecă</span>
      <h2>Media</h2>
      <p className="helper-text">JPEG, PNG, WebP sau GIF, maximum 12 MB.</p>
      <label>Text alternativ<input value={altText} onChange={(event) => setAltText(event.target.value)} placeholder="Descrierea imaginii" /></label>
      <label className="upload-button">{uploading ? 'Se încarcă…' : '＋ Încarcă imagine'}
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploading} onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setUploading(true);
          try {
            const asset = await api.uploadMedia(site.id, file, altText);
            setAssets((current) => [asset, ...current]);
            setAltText('');
          } catch (reason) {
            onError(reason instanceof Error ? reason.message : 'Imaginea nu a putut fi încărcată.');
          } finally {
            setUploading(false);
            event.target.value = '';
          }
        }} />
      </label>
      <div className="media-grid">
        {assets.map((asset) => (
          <button key={asset.id} onClick={() => onPick(asset)} title={`Folosește ${asset.filename}`}>
            <img src={asset.url} alt={asset.alt_text} />
            <span>{asset.filename}</span>
            <small>{asset.width} × {asset.height}</small>
          </button>
        ))}
      </div>
      {assets.length === 0 ? <div className="mini-empty">Nu există încă imagini.</div> : null}
    </div>
  );
}

function RevisionPanel({site, page, onRefresh, onError}: {
  site: Site; page: Page; onRefresh: () => Promise<void>; onError: (message: string) => void;
}) {
  const [revisions, setRevisions] = useState<PageRevision[]>([]);
  const [restoring, setRestoring] = useState<number | null>(null);

  const loadRevisions = async () => {
    try { setRevisions(await api.revisions(page.id)); } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Istoricul paginii nu a putut fi încărcat.');
    }
  };
  useEffect(() => { void loadRevisions(); }, [page.id, page.version]);

  return (
    <div className="inspector-content settings-panel">
      <span className="section-kicker">Editorial</span>
      <h2>Istoric</h2>
      <p className="helper-text">Restaurarea creează o versiune nouă; istoricul existent rămâne disponibil.</p>
      <div className="revision-list">
        {revisions.map((revision) => (
          <section className={revision.version === page.version ? 'revision current' : 'revision'} key={revision.version}>
            <div><strong>Versiunea {revision.version}</strong><small>{formatDate(revision.created_at)}</small></div>
            {revision.version === page.version ? <span>Curentă</span> : (
              <button disabled={restoring !== null} onClick={async () => {
                setRestoring(revision.version);
                try {
                  await api.restoreRevision(site, page, revision.version);
                  await onRefresh();
                } catch (reason) {
                  onError(reason instanceof Error ? reason.message : 'Versiunea nu a putut fi restaurată.');
                } finally { setRestoring(null); }
              }}>{restoring === revision.version ? 'Se restaurează…' : 'Restaurează'}</button>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function PluginsPanel({site, onRefresh, onError}: {
  site: Site; onRefresh: () => Promise<void>; onError: (message: string) => void;
}) {
  const [catalog, setCatalog] = useState<PluginDefinition[]>([]);
  const [configured, setConfigured] = useState<SitePlugin[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Record<string, unknown>>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const loadPlugins = async () => {
    try {
      const [definitions, active] = await Promise.all([api.pluginCatalog(), api.plugins(site.id)]);
      setCatalog(definitions);
      setConfigured(active);
      setDrafts(Object.fromEntries(active.map((plugin) => [plugin.plugin_id, plugin.configuration])));
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Pluginurile nu au putut fi încărcate.');
    }
  };
  useEffect(() => { void loadPlugins(); }, [site.id]);

  return (
    <div className="inspector-content settings-panel">
      <span className="section-kicker">Extensii aprobate</span>
      <h2>Pluginuri</h2>
      <p className="helper-text">Activează doar extensii verificate de platformă pentru acest site.</p>
      {catalog.map((definition) => {
        const saved = configured.find((plugin) => plugin.plugin_id === definition.id);
        const values = drafts[definition.id] ?? saved?.configuration ?? {};
        return (
          <section className="setting-card" key={definition.id}>
            <div className="setting-card-title">
              <div><strong>{definition.name}</strong><small>{definition.category}</small></div>
              <input
                aria-label={`Activează ${definition.name}`}
                type="checkbox"
                checked={saved?.enabled ?? false}
                disabled={busy !== null}
                onChange={async (event) => {
                  setBusy(definition.id);
                  try {
                    await api.configurePlugin(site, definition.id, event.target.checked, values);
                    await onRefresh();
                    await loadPlugins();
                  } catch (reason) {
                    onError(reason instanceof Error ? reason.message : 'Pluginul nu a putut fi configurat.');
                  } finally { setBusy(null); }
                }}
              />
            </div>
            <p>{definition.description}</p>
            {Object.entries(definition.settings).map(([name, schema]) => (
              <label key={name}>{humanize(name)}
                {schema.type === 'boolean' ? (
                  <input type="checkbox" checked={Boolean(values[name])} onChange={(event) => setDrafts({
                    ...drafts, [definition.id]: {...values, [name]: event.target.checked},
                  })} />
                ) : (
                  <input value={String(values[name] ?? '')} onChange={(event) => setDrafts({
                    ...drafts, [definition.id]: {...values, [name]: event.target.value},
                  })} />
                )}
              </label>
            ))}
            {Object.keys(definition.settings).length > 0 ? (
              <button className="secondary-button compact" disabled={busy !== null} onClick={async () => {
                setBusy(definition.id);
                try {
                  await api.configurePlugin(site, definition.id, saved?.enabled ?? false, values);
                  await onRefresh();
                  await loadPlugins();
                } catch (reason) {
                  onError(reason instanceof Error ? reason.message : 'Configurația nu a putut fi salvată.');
                } finally { setBusy(null); }
              }}>Salvează configurarea</button>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function DomainsPanel({site, onRefresh, onError}: {
  site: Site; onRefresh: () => Promise<void>; onError: (message: string) => void;
}) {
  const [domains, setDomains] = useState<SiteDomain[]>([]);
  const [hostname, setHostname] = useState('');
  const [busy, setBusy] = useState(false);

  const loadDomains = async () => {
    try { setDomains(await api.domains(site.id)); } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Domeniile nu au putut fi încărcate.');
    }
  };
  useEffect(() => { void loadDomains(); }, [site.id]);

  const mutate = async (operation: () => Promise<SiteDomain>) => {
    setBusy(true);
    try {
      await operation();
      await onRefresh();
      await loadDomains();
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Domeniul nu a putut fi actualizat.');
    } finally { setBusy(false); }
  };

  return (
    <div className="inspector-content settings-panel">
      <span className="section-kicker">Publicare</span>
      <h2>Domenii</h2>
      <p className="helper-text">Leagă un domeniu și confirmă proprietatea lui printr-o înregistrare DNS TXT.</p>
      <label>Domeniu nou<input placeholder="www.exemplu.ro" value={hostname} onChange={(event) => setHostname(event.target.value)} /></label>
      <button className="primary-button full" disabled={busy || !hostname.trim()} onClick={() => void mutate(async () => {
        const domain = await api.addDomain(site, hostname);
        setHostname('');
        return domain;
      })}>Adaugă domeniul</button>
      {domains.map((domain) => (
        <section className="setting-card domain-card" key={domain.id}>
          <div className="setting-card-title"><strong>{domain.hostname}</strong><span className={`domain-state ${domain.status}`}>{domain.primary ? 'Principal' : domain.status === 'verified' ? 'Verificat' : 'În așteptare'}</span></div>
          {domain.status === 'pending' ? (
            <div className="dns-record">
              <small>{domain.verification.record_type}</small>
              <code>{domain.verification.record_name}</code>
              <code>{domain.verification.record_value}</code>
              <button className="secondary-button compact" disabled={busy} onClick={() => void mutate(() => api.verifyDomain(site, domain.id))}>Verifică DNS</button>
            </div>
          ) : !domain.primary ? (
            <button className="secondary-button compact" disabled={busy} onClick={() => void mutate(() => api.makePrimaryDomain(site, domain.id))}>Setează principal</button>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function BlockInspector({block, definition, onChange, onDelete}: {
  block: Block; definition: BlockDefinition; onChange: (block: Block) => void; onDelete: () => void;
}) {
  const properties = definition.property_schema.properties ?? {};
  return (
    <div className="inspector-content">
      <span className="section-kicker">{definition.category}</span>
      <h2>{definition.label}</h2>
      {Object.entries(properties).map(([name, schema]) => (
        <label key={name}>{humanize(name)}
          {schema.type === 'array' || schema.type === 'object' ? (
            <JsonPropertyEditor
              value={block.props[name] ?? defaultPropertyValue(schema)}
              schema={schema}
              onChange={(value) => onChange({...block, props: {...block.props, [name]: value}})}
            />
          ) : name === 'body' || name === 'content' || name === 'description' || name === 'note' ? (
            <textarea value={String(block.props[name] ?? '')} onChange={(event) => onChange({...block, props: {...block.props, [name]: event.target.value}})} />
          ) : (
            <input
              type={schema.type === 'integer' ? 'number' : 'text'}
              value={String(block.props[name] ?? '')}
              onChange={(event) => onChange({...block, props: {...block.props, [name]: schema.type === 'integer' ? Number(event.target.value) : event.target.value}})}
            />
          )}
        </label>
      ))}
      {definition.allowed_styles.length > 0 ? <h3 className="subheading">Aspect</h3> : null}
      {definition.allowed_styles.map((style) => (
        <label key={style}>{humanize(style)}
          <input value={block.styles?.[style] ?? ''} onChange={(event) => onChange({...block, styles: {...block.styles, [style]: event.target.value}})} />
        </label>
      ))}
      <button className="danger-button" onClick={onDelete}>Șterge blocul</button>
    </div>
  );
}

function JsonPropertyEditor({value, schema, onChange}: {
  value: unknown; schema: PropertySchema; onChange: (value: unknown) => void;
}) {
  const formatted = JSON.stringify(value, null, 2) ?? '';
  const [draft, setDraft] = useState(formatted);
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setDraft(formatted);
    setInvalid(false);
  }, [formatted]);

  const commit = () => {
    try {
      const parsed: unknown = JSON.parse(draft);
      if ((schema.type === 'array' && !Array.isArray(parsed))
        || (schema.type === 'object' && (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object'))) {
        setInvalid(true);
        return;
      }
      setInvalid(false);
      onChange(parsed);
    } catch {
      setInvalid(true);
    }
  };

  return <><textarea className={invalid ? 'json-editor invalid' : 'json-editor'} value={draft}
    onChange={(event) => setDraft(event.target.value)} onBlur={commit} />
    <small className={invalid ? 'json-help invalid' : 'json-help'}>
      {invalid ? 'JSON invalid — corectează structura înainte de salvare.' : 'Date structurate JSON; modificările se aplică la ieșirea din câmp.'}
    </small></>;
}

function ThemePanel({site, onSaved}: {site: Site; onSaved: (theme: Theme) => void}) {
  const [theme, setTheme] = useState<Theme>(() => ({
    preset: site.theme.preset,
    tokens: {...site.theme.tokens, colors: {...defaultColors, ...site.theme.tokens.colors}},
  }));
  useEffect(() => {
    setTheme({preset: site.theme.preset, tokens: {...site.theme.tokens, colors: {...defaultColors, ...site.theme.tokens.colors}}});
  }, [site.id, site.version]);
  return (
    <div className="inspector-content">
      <span className="section-kicker">Design system</span>
      <h2>Temă</h2>
      <label>Preset<select value={theme.preset} onChange={(event) => setTheme({...theme, preset: event.target.value})}>
        <option value="impakt-modern">Impakt Modern</option>
        <option value="editorial">Editorial</option>
        <option value="minimal">Minimal</option>
      </select></label>
      <h3 className="subheading">Culori</h3>
      <div className="color-grid">
        {Object.entries(theme.tokens.colors ?? defaultColors).map(([key, value]) => (
          <label key={key}><span>{humanize(key)}</span><input type="color" value={value} onChange={(event) => setTheme({
            ...theme, tokens: {...theme.tokens, colors: {...theme.tokens.colors, [key]: event.target.value.toUpperCase()}},
          })} /></label>
        ))}
      </div>
      <button className="primary-button full" onClick={() => onSaved(theme)}>Aplică tema</button>
    </div>
  );
}

function PreviewBlock({block}: {block: Block}) {
  const p = block.props;
  if (block.type === 'core.hero') return <section className="preview-hero"><small>{String(p.eyebrow ?? 'Povestea ta începe aici')}</small><h1>{String(p.heading || 'Un titlu care inspiră')}</h1><p>{String(p.body ?? 'Adaugă un mesaj clar pentru vizitatorii site-ului.')}</p>{p.button_label ? <button>{String(p.button_label)}</button> : null}</section>;
  if (block.type === 'core.rich-text') return <section className="preview-text"><p>{String(p.content || 'Scrie conținutul acestei secțiuni.')}</p></section>;
  if (block.type === 'core.image') return <figure className="preview-image">{p.source ? <img src={String(p.source)} alt={String(p.alt ?? '')} /> : <div>Imagine</div>}<figcaption>{String(p.caption ?? '')}</figcaption></figure>;
  if (block.type === 'core.cta') return <section className="preview-cta"><h2>{String(p.heading || 'Pregătit să începi?')}</h2><p>{String(p.body ?? '')}</p><button>{String(p.button_label || 'Continuă')}</button></section>;
  if (block.type === 'core.quick-links') {
    const items = Array.isArray(p.items) ? p.items : [];
    return <section className="preview-structured"><small>{String(p.eyebrow ?? '')}</small><h2>{String(p.heading || 'Linkuri rapide')}</h2><div className="preview-card-grid">{items.map((item, index) => {
      const entry = typeof item === 'object' && item !== null ? item as Record<string, unknown> : {};
      return <article key={index}><b>{String(entry.label ?? '')}</b><p>{String(entry.description ?? '')}</p><span>↗</span></article>;
    })}</div></section>;
  }
  if (block.type === 'core.stats') {
    const items = Array.isArray(p.items) ? p.items : [];
    return <section className="preview-stats"><h2>{String(p.heading ?? '')}</h2><div>{items.map((item, index) => {
      const entry = typeof item === 'object' && item !== null ? item as Record<string, unknown> : {};
      return <article key={index}><strong>{String(entry.value ?? '')}</strong><span>{String(entry.label ?? '')}</span></article>;
    })}</div></section>;
  }
  if (block.type === 'core.data-table') {
    const columns = Array.isArray(p.columns) ? p.columns : [];
    const rows = Array.isArray(p.rows) ? p.rows : [];
    return <section className="preview-table"><small>{String(p.eyebrow ?? '')}</small><h2>{String(p.heading || 'Tabel de date')}</h2><table><thead><tr>{columns.map((column, index) => <th key={index}>{String(column)}</th>)}</tr></thead><tbody>{rows.slice(0, 5).map((row, rowIndex) => <tr key={rowIndex}>{(Array.isArray(row) ? row : []).map((cell, cellIndex) => <td key={cellIndex}>{String(cell)}</td>)}</tr>)}</tbody></table></section>;
  }
  if (block.type === 'core.container') return <section className="preview-container">Container cu {String(p.columns ?? 1)} coloane</section>;
  return <section className="preview-unknown">{block.type}</section>;
}

function StatusPill({site}: {site: Site}) {
  return <div className={`status-pill ${site.status}`}><span />{site.status === 'published' ? 'Publicat' : 'Draft'} · v{site.version}</div>;
}

function Loading() {
  return <div className="loading"><span /><p>Se încarcă Website Studio…</p></div>;
}

function themeVariables(theme: Theme) {
  const colors = {...defaultColors, ...theme.tokens.colors};
  return {
    '--site-primary': colors.primary,
    '--site-secondary': colors.secondary,
    '--site-accent': colors.accent,
    '--site-bg': colors.background,
    '--site-surface': colors.surface,
    '--site-text': colors.text,
    '--site-muted': colors.muted,
    '--site-border': colors.border,
  } as CSSProperties;
}

function humanize(value: string) {
  return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase());
}

function slugify(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 160);
}

function defaultPropertyValue(schema?: PropertySchema): unknown {
  if (schema?.type === 'integer') return schema.minimum ?? 1;
  if (schema?.type === 'number') return schema.minimum ?? 0;
  if (schema?.type === 'boolean') return false;
  if (schema?.type === 'array') return [];
  if (schema?.type === 'object') return {};
  return '';
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('ro-RO', {dateStyle: 'medium', timeStyle: 'short'}).format(new Date(value));
}

function blockIcon(type: string) {
  if (type.includes('hero')) return 'H';
  if (type.includes('text')) return '¶';
  if (type.includes('image')) return '▧';
  if (type.includes('cta')) return '→';
  if (type.includes('container')) return '▦';
  return '◇';
}
